import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card } from '../design'
import { createProject } from '../api/projects'
import { ApiError } from '../api/client'

const COLORS = ['#7e57c2', '#6b9bd1', '#d97757', '#e8c468', '#8aa572', '#9aa0a6', '#c47d9d', '#5e8a8c']

export default function NewProject() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [keyDraft, setKeyDraft] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [description, setDescription] = useState('')
  const [color, setColor] = useState(COLORS[0])
  const [error, setError] = useState<string | null>(null)

  // Auto-derive key from name until user types in the key field manually
  const autoKey = derivedKey(name)
  const effectiveKey = keyTouched ? keyDraft : autoKey

  const createMut = useMutation({
    mutationFn: () => createProject({
      name: name.trim(),
      key: effectiveKey.toUpperCase().replace(/[^A-Z0-9]/g, ''),
      description: description.trim() || null,
      color,
    }),
    onSuccess: (proj) => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['lookups', 'projects'] })
      nav(`/projects/${proj.key}`)
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.details) {
        const firstKey = Object.keys(e.details)[0]
        if (firstKey) {
          setError(`${firstKey}: ${e.details[firstKey][0]}`)
          return
        }
      }
      setError(e.message)
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) { setError('Name is required'); return }
    // Match backend sanitization: strip non-alphanumeric, then enforce 2-10.
    const sanitized = effectiveKey.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (sanitized.length < 2 || sanitized.length > 10) {
      setError('Key must be 2-10 alphanumeric chars (after stripping symbols)')
      return
    }
    createMut.mutate()
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-6">
      <h1 className="text-xl font-semibold tracking-tight mb-1">New project</h1>
      <p className="text-sm text-fg-subtle mb-5">
        Creates a project plus its default workflow (Todo → In Progress → In Review → Done) and a kanban board.
      </p>

      <Card>
        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Name</span>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              required
              maxLength={150}
              placeholder="Atlas"
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            />
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">
              Key <span className="font-mono text-fg-subtle">(2-10 chars, used in story IDs e.g. <span className="text-fg-muted">{effectiveKey || 'ATL'}-1</span>)</span>
            </span>
            <input
              value={effectiveKey}
              onChange={e => { setKeyDraft(e.target.value.toUpperCase()); setKeyTouched(true) }}
              maxLength={10}
              placeholder={autoKey || 'ATL'}
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            />
            {!keyTouched && (
              <span className="block text-[10px] text-fg-subtle mt-1">
                Auto-derived from name. Click to override.
              </span>
            )}
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Description (optional)</span>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="What is this project for?"
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-y"
            />
          </label>

          <div>
            <span className="block text-xs font-medium text-fg-muted mb-2">Color</span>
            <div className="flex flex-wrap gap-2">
              {COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  aria-label={`Pick ${c}`}
                  className={
                    'h-8 w-8 rounded-full ring-2 ring-offset-2 ring-offset-bg transition ' +
                    (color === c ? 'ring-accent' : 'ring-transparent hover:ring-border')
                  }
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-border">
            <Button type="button" variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? 'Creating…' : 'Create project'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

function derivedKey(name: string): string {
  // Take first letter of each word, fall back to first 3 chars; uppercase, alphanumeric only.
  const cleaned = name.replace(/[^a-zA-Z0-9 ]/g, '').trim()
  if (!cleaned) return ''
  const words = cleaned.split(/\s+/).filter(Boolean)
  let k = ''
  if (words.length >= 2) k = words.map(w => w[0]).join('')
  else k = words[0].slice(0, 4)
  k = k.toUpperCase().replace(/[^A-Z0-9]/g, '')
  return k.slice(0, 10)
}
